import { useMutation, useQueryClient } from '@tanstack/react-query';

import { secretKeys } from '@/features/secrets';
import { deleteConnection } from '@/lib/api/generated/api';

import { toConnectionError } from '../errors';
import { connectionKeys } from '../queryKeys';

/**
 * Remove a connection outright — regardless of whether trigger profiles
 * still reference it (FR-006). The backend applies no reference-count
 * guard, matching existing workspace-secret deletion behavior
 * (`useDeleteWorkspaceSecret`); this hook does not add one either. Any
 * trigger profile left pointing at a deleted connection surfaces that on its
 * *own* next scheduled run as a sanitized failure, not as a blocked delete
 * here.
 */
export function useDeleteConnection(workspaceId: string) {
  const queryClient = useQueryClient();

  return useMutation({
    mutationFn: async (connectionId: string): Promise<void> => {
      const response = await deleteConnection(workspaceId, connectionId);
      if (response.status === 204) return;
      throw toConnectionError(response.status, response.data);
    },
    onSuccess: (_data, connectionId) => {
      // Dropped, not invalidated: re-testing a connection that no longer exists
      // answers 404.
      queryClient.removeQueries({
        queryKey: connectionKeys.health(workspaceId, connectionId),
      });
      void queryClient.invalidateQueries({
        queryKey: connectionKeys.all(workspaceId),
      });
      // A catalog connection's managed entries are deleted with it (cascade).
      void queryClient.invalidateQueries({
        queryKey: secretKeys.all(workspaceId),
      });
    },
  });
}
